import { toast } from 'react-toastify';
import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { backendUrl } from '../constants/urls';

export interface TransferRequest {
	id: string;
	approvedBy: string | null;
	fromLocationCode: string;
	toLocationCode: string | null;
	fromUserId: string | null;
	toUserId: string;
	amount: string;
	transferDate: string;
	type: string;
	status: 'pending' | 'approved' | 'rejected';
	createdAt: string;
	updatedAt: string;
	fromLocation: {
		id: string;
		locationName: string;
		locationCode: string;
	};
	toUser: {
		fullname: string;
		id: string;
		firstname: string;
		lastname: string;
		email: string;
	};
}

export interface TransferRequestsResponse {
	success: boolean;
	message: string;
	data: TransferRequest[];
	pagination: {
		total: number;
		totalPages: number;
		currentPage: number;
		limit: number;
		hasNextPage: boolean;
		hasPrevPage: boolean;
	};
	stats?: {
		total: number;
		pending: number;
		approved: number;
		rejected: number;
	};
}

export interface InitiateTransferPayload {
	transferType: 'currentLocationToMe' | 'currentLocationToOtherUser' | 'otherLocationToOtherUser' | 'meToCurrentLocation';
	amount: number;
	userId?: string;
	fromLocationId?: string;
	toUserId?: string;
	currentUserId: string;
	currentLocationId: string;
}

// Fetch transfer requests
export const fetchTransferRequests = createAsyncThunk(
	'transfers/fetchTransferRequests',
	async ({ page = 1, limit = 10, status }: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				...(status && status !== 'all' && { status }),
			});

			const response = await api.get(`${backendUrl}/admin/locations/transfer/initiated-requests?${params}`);
			return response.data as TransferRequestsResponse;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to fetch transfer requests';
			return rejectWithValue(errorMessage);
		}
	}
);

export const fetchVaultToUserTransferRequests = createAsyncThunk(
	'transfers/fetchVaultToUserTransferRequests',
	async ({ page = 1, limit = 10, status }: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				...(status && status !== 'all' && { status }),
			});

			const response = await api.get(`${backendUrl}/admin/locations/transfer/vault-to-user-requests?${params}`);
			return response.data as TransferRequestsResponse;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to fetch vault to user transfer requests';
			return rejectWithValue(errorMessage);
		}
	}
);

export const fetchUserToVaultTransferRequests = createAsyncThunk(
	'transfers/fetchUserToVaultTransferRequests',
	async ({ page = 1, limit = 10, status }: { page?: number; limit?: number; status?: string }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				...(status && status !== 'all' && { status }),
			});

			const response = await api.get(`${backendUrl}/admin/locations/transfer/user-to-vault-requests?${params}`);
			return response.data as TransferRequestsResponse;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to fetch user to vault transfer requests';
			return rejectWithValue(errorMessage);
		}
	}
);

export const initiateTransferRequest = createAsyncThunk(
	'transfers/initiateTransferRequest',
	async (payload: InitiateTransferPayload, { rejectWithValue }) => {
		try {
			let endpoint = '';
			let requestPayload: any = {
				amount: payload.amount,
			};

			switch (payload.transferType) {
				case 'currentLocationToMe':
					endpoint = `/admin/locations/transfer/location/user/${payload.currentLocationId}`;
					requestPayload.userId = payload.currentUserId;
					break;
				case 'currentLocationToOtherUser':
					endpoint = `/admin/locations/transfer/location/user/${payload.currentLocationId}`;
					requestPayload.userId = payload.userId;
					break;
				case 'otherLocationToOtherUser':
					endpoint = `/admin/locations/transfer/location/user/${payload.fromLocationId}`;
					requestPayload.userId = payload.toUserId;
					break;
				case 'meToCurrentLocation':
					endpoint = `/admin/locations/transfer/user-to-location/${payload.currentLocationId}`;
					break;
				default:
					throw new Error('Invalid transfer type');
			}

			const response = await api.post(`${backendUrl}${endpoint}`, requestPayload);
			return response.data;
		} catch (error: any) {
            console.log("Transfer Error", error)
			const errorMessage = error?.response?.data?.message || error?.response?.data?.error || 'Failed to initiate transfer request';
			return rejectWithValue(errorMessage);
		}
	}
);

// Approve transfer request
export const approveTransferRequest = createAsyncThunk(
	'transfers/approveTransferRequest',
	async ({ requestId, transferType }: { requestId: string; transferType: 'vaultToUser' | 'userToVault' }, { rejectWithValue }) => {
		try {
			const endpoint = transferType === 'vaultToUser' 
				? `/admin/locations/transfer/location/user/approve/${requestId}`
				: `/admin/locations/transfer/user-to-location/approve/${requestId}`;
			
			const response = await api.put(`${backendUrl}${endpoint}`);
			return { requestId, data: response.data };
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to approve transfer request';
			return rejectWithValue(errorMessage);
		}
	}
);

export const rejectTransferRequest = createAsyncThunk(
	'transfers/rejectTransferRequest',
	async ({ requestId, transferType }: { requestId: string; transferType: 'vaultToUser' | 'userToVault' }, { rejectWithValue }) => {
		try {
			const endpoint = transferType === 'vaultToUser' 
				? `/admin/locations/transfer/location/user/reject/${requestId}`
				: `/admin/locations/transfer/user-to-location/reject/${requestId}`;
			
			const response = await api.put(`${backendUrl}${endpoint}`);
			return { requestId, data: response.data };
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to reject transfer request';
			return rejectWithValue(errorMessage);
		}
	}
);

// Fetch users for dropdown
export const fetchUsersForTransfer = createAsyncThunk(
	'transfers/fetchUsersForTransfer',
	async (_, { rejectWithValue }) => {
		try {
			const response = await api.get(`${backendUrl}/admin/users?limit=100`);
			return response.data;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to fetch users';
			return rejectWithValue(errorMessage);
		}
	}
);

// Fetch locations for dropdown
export const fetchLocationsForTransfer = createAsyncThunk(
	'transfers/fetchLocationsForTransfer',
	async (_, { rejectWithValue }) => {
		try {
			const response = await api.get(`${backendUrl}/admin/locations`);
			return response.data;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to fetch locations';
			return rejectWithValue(errorMessage);
		}
	}
);

// Fetch vault transactions
export const fetchVaultTransactions = createAsyncThunk(
	'transfers/fetchVaultTransactions',
	async ({ page = 1, limit = 10, type, startDate, endDate }: { page?: number; limit?: number; type?: string; startDate?: string; endDate?: string }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				...(type && type !== 'all' && { type }),
				...(startDate && { startDate }),
				...(endDate && { endDate }),
			});

			const response = await api.get(`${backendUrl}/admin/locations/get/all/vault-transactions?${params}`);
			return response.data;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to fetch vault transactions';
			return rejectWithValue(errorMessage);
		}
	}
);

export const exportVaultTransactions = createAsyncThunk(
	'transfers/exportVaultTransactions',
	async ({ type, startDate, endDate }: { type?: string; startDate?: string; endDate?: string }, { rejectWithValue }) => {
		try {
			const params = new URLSearchParams({
				...(type && type !== 'all' && { type }),
				...(startDate && { startDate }),
				...(endDate && { endDate }),
			});

			const response = await api.get(`${backendUrl}/admin/locations/export/vault-transactions?${params}`);
			return response.data;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to export vault transactions';
			return rejectWithValue(errorMessage);
		}
	}
);

export const refreshCurrentUser = createAsyncThunk(
	'auth/refreshCurrentUser',
	async (_, { rejectWithValue, getState }) => {
		try {
			const state = getState() as any;
			const token = state.auth.user?.token;
			
			if (!token) {
				return rejectWithValue('No authentication token found');
			}

			const response = await api.get(`${backendUrl}/admin/auth/getMe`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});
			return response.data;
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || 'Failed to refresh user data';
			return rejectWithValue(errorMessage);
		}
	}
);

// Show toast notifications
export const showTransferToast = (message: string, type: 'success' | 'error', darkMode: boolean) => {
	const toastOptions = {
		position: 'top-right' as const,
		theme: darkMode ? 'dark' as const : 'light' as const,
	};

	if (type === 'success') {
		toast.success(message, toastOptions);
	} else {
		toast.error(message, toastOptions);
	}
};